import React, { useState, useEffect } from 'react';
import { TextField, InputAdornment, Popover, Box, Typography, Button } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface TimeRangeInputProps {
  field: string;
  formData: any;
  onChange: (field: string, value: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  endAdornment?: React.ReactNode;
  error?: string;
}

const TimeRangeInput: React.FC<TimeRangeInputProps> = ({
  field,
  formData,
  onChange,
  label,
  required = false,
  placeholder = "e.g., 9:00 AM - 6:00 PM",
  endAdornment,
  error
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);

  const open = Boolean(anchorEl);

  // Parse existing time range when component mounts or formData changes
  useEffect(() => {
    const timeRange = formData[field] || '';
    if (timeRange) {
      const match = timeRange.match(/(\d{1,2}:\d{2}\s*[AP]M)\s*to\s*(\d{1,2}:\d{2}\s*[AP]M)/i);
      if (match) {
        setStartTime(dayjs(match[1], 'hh:mm A'));
        setEndTime(dayjs(match[2], 'hh:mm A'));
      }
    } else {
      setStartTime(null);
      setEndTime(null);
    }
  }, [formData[field], field]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleTimeChange = (newStartTime: any, newEndTime: any) => {
    const start = newStartTime ? (newStartTime.format ? newStartTime : dayjs(newStartTime)) : null;
    const end = newEndTime ? (newEndTime.format ? newEndTime : dayjs(newEndTime)) : null;
    
    setStartTime(start);
    setEndTime(end);
    
    if (start && end) {
      const timeRange = `${start.format('hh:mm A')} to ${end.format('hh:mm A')}`;
      onChange(field, timeRange);
    } else {
      onChange(field, '');
    }
  };

  const handleApply = () => {
    handleClose();
  };

  const handleClear = () => {
    setStartTime(null);
    setEndTime(null);
    onChange(field, '');
    handleClose();
  };

  return (
    <>
      <TextField
        fullWidth
        size="small"
        variant="outlined"
        label={label}
        required={required}
        value={formData[field] || ''}
        placeholder={placeholder}
        onClick={handleClick}
        error={!!error}
        helperText={error}
        InputProps={{
          endAdornment: endAdornment,
          readOnly: true,
          sx: {
            cursor: 'pointer',
            '& input': {
              cursor: 'pointer',
            },
          },
        }}
        InputLabelProps={{
          sx: {
            "&.MuiInputLabel-asterisk": {
              color: "red",
            },
          },
        }}
        sx={{
          "& .MuiFormLabel-asterisk": {
            color: "red",
          },
        }}
      />
      
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
          },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
              Select Operating Hours
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              <TimePicker
                label="Start Time"
                value={startTime}
                onChange={(newValue) => handleTimeChange(newValue, endTime)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
                format="hh:mm A"
              />
              
              <Typography variant="body2" sx={{ color: "rgba(0, 0, 0, 0.6)", fontWeight: 500 }}>
                to
              </Typography>
              
              <TimePicker
                label="End Time"
                value={endTime}
                onChange={(newValue) => handleTimeChange(startTime, newValue)}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                  },
                }}
                format="hh:mm A"
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={handleClear}>
                Clear
              </Button>
              <Button size="small" variant="contained" onClick={handleApply}>
                Apply
              </Button>
            </Box>
          </Box>
        </LocalizationProvider>
      </Popover>
    </>
  );
};

export default TimeRangeInput;
